import { FormModal, SubmitProps } from '@/components/form-modal.js';
import { FormInputFile } from '@/components/forms/form-input-file.js';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert.js';
import { useToast } from '@/components/ui/hooks/use-toast.js';
import { createAssessmentContentsByCsv, getAssessmentContents, getAssessments } from '@/lib/api-calls.js';
import { csvFormSchema, parseCsvAndUpload } from '@/lib/csv-utils.js';
import { pluralize } from '@/lib/string-utils.js';
import { CreateAssessmentContentCsvFormKeys, createAssessmentContentCsvRow } from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, AlertTriangle } from 'lucide-react';
import { ReactNode, useState } from 'react';
import { z } from 'zod';

interface CreateAssessmentContentsByCsvProps {
  trigger: ReactNode;
}

const EMPTY_ERRORS: string[] = [];
const formSchema = csvFormSchema;

export const CreateAssessmentContentsByCsv = ({ trigger }: CreateAssessmentContentsByCsvProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [errorMessages, setErrorMessages] = useState<string[]>(EMPTY_ERRORS);
  const { mutateAsync } = useMutation({
    mutationFn: createAssessmentContentsByCsv,
  });

  const onSubmit = async ({ formData, closeModal }: SubmitProps<z.infer<typeof formSchema>>) => {
    const { csvFile } = formData;

    const result = await parseCsvAndUpload({
      file: csvFile[0],
      rowSchema: createAssessmentContentCsvRow,
      onUpload: async (file) => {
        const uploadForm = new FormData();
        uploadForm.append(CreateAssessmentContentCsvFormKeys.csvFile, file);
        return mutateAsync(uploadForm);
      },
      onToast: toast,
      onSetErrors: setErrorMessages,
    });

    const { successRowsCount, hasErrors } = result;

    if (successRowsCount > 0) {
      toast({
        title: `${successRowsCount} ${pluralize(successRowsCount, 'assessment content', 'assessment contents')} created`,
      });
      queryClient.invalidateQueries(getAssessmentContents());
      queryClient.invalidateQueries(getAssessments());
    }

    if (!hasErrors) {
      closeModal();
    }

    return successRowsCount;
  };

  return (
    <FormModal
      modalTitle="Create assessment contents by CSV"
      formSchema={formSchema}
      onSubmit={onSubmit}
      trigger={trigger}
      onClose={() => setErrorMessages(EMPTY_ERRORS)}
    >
      {({ form }) => {
        return (
          <>
            <Alert variant="warning" className="items-start">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Assessment changes might take up to 30 minutes to reflect on various UI through the app.
              </AlertDescription>
            </Alert>
            <div className="grid grid-cols-1 gap-2 py-4">
              <FormInputFile
                form={form}
                name="csvFile"
                label="Assessment contents CSV"
                inputProps={{
                  accept: '.csv',
                  className: 'w-full',
                }}
              />
              <span>
                See{' '}
                <a
                  href="https://docs.google.com/spreadsheets/d/1rzbm2t8r1Wsa89DuYZEippWqLki20n_XV6Hp0KelsPw/edit?gid=1573606221#gid=1573606221"
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-blue-600 hover:underline"
                >
                  template
                </a>{' '}
                for CSV template. Export the sheet as csv file and edit it. Do NOT change the header row
              </span>
            </div>
            {errorMessages.length > 0 && (
              <div className="w-full mb-4">
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Error while parsing CSV</AlertTitle>
                  <AlertDescription>
                    {errorMessages.map((message, index) => (
                      <p key={index}>{message}</p>
                    ))}
                  </AlertDescription>
                </Alert>
              </div>
            )}
          </>
        );
      }}
    </FormModal>
  );
};
